import { ChevronDown, Trophy, Users } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useOdlCount } from '@/hooks/useOdlCount';
import { getHeroStats } from '@/config/heroStats';


const BOARD = [
  { name: 'Monika Kochowicz', role: 'Prezeska' },
  { name: 'Monika Kierzkowska', role: 'Wiceprezeska' },
  { name: 'Agnieszka Sankiewicz', role: 'Skarbnik' },
  { name: 'Bartłomiej Kochowicz', role: 'Sekretarz' },
];

const AboutSection = () => {
  const odlCount = useOdlCount();
  const [yearsStat, odlStat, projectsStat] = getHeroStats(odlCount);

  return (
    <section id="o-nas" className="py-16 lg:py-24 bg-background">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">O organizacji</p>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-6">Ponad 20 lat na rzecz wspólnoty</h2>

            <p className="text-muted-foreground leading-relaxed mb-6">
              Kuźnia Inicjatyw Lokalnych w Lisewie (dawniej Stowarzyszenie Przyjaciół Gminy Lisewo)
              działa od 2003 roku. Przez dwie dekady budowaliśmy lokalne więzi, wspieraliśmy inicjatywy
              mieszkańców i realizowaliśmy projekty, które realnie zmieniały codzienne życie.
            </p>

            <blockquote className="border-l-4 border-secondary pl-4 py-2 my-6 italic text-foreground/80">
              „Kuźnia to nie tylko nazwa — to filozofia. Kujesz, kiedy materiał jest gorący.
              Działamy tam, gdzie jest energia i chęć zmiany."
            </blockquote>

            <p className="text-muted-foreground leading-relaxed mb-6">
              Od 2015 roku jesteśmy Ośrodkiem Działaj Lokalnie — jednym z {odlCount} ODL w Polsce.
              Organizujemy konkurs grantowy, szkolimy wnioskodawców i pomagamy rozliczać projekty.
              Do dziś wsparliśmy ponad 50 inicjatyw w naszym regionie.
            </p>

            <p className="text-muted-foreground leading-relaxed">
              Naszym sztandarowym projektem była Lisewska Bezprzewodowa Sieć Informacyjna (2007–2013) —
              pionierska cyfryzacja gminy, nagrodzona przez Marszałka Województwa tytułem
              „Rodzynki z pozarządówki" 2009.
            </p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-primary rounded-lg p-6 text-center text-primary-foreground">
                <p className="text-3xl font-bold font-heading">2003</p>
                <p className="text-sm mt-1 opacity-90">Rok założenia</p>
              </div>
              <div className="bg-secondary rounded-lg p-6 text-center text-secondary-foreground">
                <p className="text-3xl font-bold font-heading">{odlStat.value}{odlStat.suffix ?? ''}</p>
                <p className="text-sm mt-1 opacity-90">{odlStat.longLabel ?? odlStat.label}</p>
              </div>
              <div className="bg-accent rounded-lg p-6 text-center text-accent-foreground">
                <p className="text-3xl font-bold font-heading">{projectsStat.value}{projectsStat.suffix ?? ''}</p>
                <p className="text-sm mt-1 opacity-90">{projectsStat.longLabel ?? projectsStat.label}</p>
              </div>
              <div className="bg-muted rounded-lg p-6 text-center">
                <p className="text-3xl font-bold font-heading text-foreground">{yearsStat.value}</p>
                <p className="text-sm mt-1 text-muted-foreground">{yearsStat.longLabel ?? yearsStat.label}</p>
              </div>
            </div>


            <div className="border-2 border-accent rounded-panel p-5 flex items-start gap-4">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Trophy className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="font-heading font-bold">Nagroda Marszałka Województwa 2009</p>
                <p className="text-sm text-muted-foreground mt-1">
                  „Rodzynki z pozarządówki" — za projekt LBSI
                </p>
              </div>
            </div>

            <Collapsible className="bg-muted rounded-panel border border-border">
              <CollapsibleTrigger className="group w-full flex items-center gap-3 p-5 rounded-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary text-left">
                <div className="w-10 h-10 bg-secondary/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-secondary" />
                </div>
                <h3 className="font-heading font-bold text-lg flex-1">Władze Stowarzyszenia</h3>
                <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="space-y-2 px-5 pb-5">
                  {BOARD.map((m) => (
                    <li key={m.name} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 sm:gap-3 py-1.5 border-b border-border/50 last:border-b-0">
                      <span className="font-semibold text-foreground">{m.name}</span>
                      <span className="text-sm text-secondary font-medium">{m.role}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
